import React, { useState, useEffect } from 'react';
import './App.css';

// API URL - use relative path for production (through nginx proxy)
const API_URL = '/api/todos';

// Cookie helper functions
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
};

const getCookie = (name) => {
  const value = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return value ? decodeURIComponent(value.pop()) : null;
};

// Generate unique user ID
const generateUserId = () => {
  return 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Get or create user ID from cookie
const getOrCreateUserId = () => {
  let userId = getCookie('todoUserId');
  if (!userId) {
    userId = generateUserId();
    setCookie('todoUserId', userId);
  }
  return userId;
};

function App() {
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [userId] = useState(getOrCreateUserId());

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      const response = await fetch(API_URL, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setTodos(data);
    } catch (error) {
      console.error('Error fetching todos:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ text: newTodo, completed: false }),
      });
      if (!response.ok) throw new Error('Failed to add');
      const data = await response.json();
      setTodos([...todos, data]);
      setNewTodo('');
    } catch (error) {
      console.error('Error adding todo:', error);
    }
  };

  const deleteTodo = async (id) => {
    try {
      await fetch(`${API_URL}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setTodos(todos.filter((todo) => todo.id !== id));
    } catch (error) {
      console.error('Error deleting todo:', error);
    }
  };

  const toggleTodo = async (id) => {
    try {
      const response = await fetch(`${API_URL}/${id}/toggle`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to toggle');
      const updatedTodo = await response.json();
      setTodos(todos.map((todo) => (todo.id === id ? updatedTodo : todo)));
    } catch (error) {
      console.error('Error toggling todo:', error);
    }
  };

  const startEditing = (todo) => {
    setEditingId(todo.id);
    setEditingText(todo.text);
  };

  const saveEdit = async (id) => {
    if (!editingText.trim()) return;

    try {
      const todo = todos.find((t) => t.id === id);
      const response = await fetch(`${API_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ text: editingText, completed: todo.completed }),
      });
      if (!response.ok) throw new Error('Failed to update');
      const updatedTodo = await response.json();
      setTodos(todos.map((t) => (t.id === id ? updatedTodo : t)));
      setEditingId(null);
      setEditingText('');
    } catch (error) {
      console.error('Error updating todo:', error);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const deleteCompletedTodos = async () => {
    try {
      const response = await fetch(`${API_URL}/completed`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete completed');
      setTodos(todos.filter((todo) => !todo.completed));
    } catch (error) {
      console.error('Error deleting completed todos:', error);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, todo) => {
    setDraggedItem(todo);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', todo.id);
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedItem(null);
    setDragOverItem(null);
    document.querySelectorAll('.todo-item').forEach(item => {
      item.classList.remove('drag-over');
    });
  };

  const handleDragOver = (e, todo) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedItem && draggedItem.id !== todo.id) {
      setDragOverItem(todo);
    }
  };

  const handleDragLeave = (e) => {
    e.target.classList.remove('drag-over');
  };

  const handleDrop = async (e, targetTodo) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetTodo.id) {
      return;
    }

    const newTodos = [...todos];
    const draggedIndex = newTodos.findIndex(t => t.id === draggedItem.id);
    const targetIndex = newTodos.findIndex(t => t.id === targetTodo.id);

    const [removed] = newTodos.splice(draggedIndex, 1);
    newTodos.splice(targetIndex, 0, removed);

    setTodos(newTodos);
    setDraggedItem(null);
    setDragOverItem(null);

    // Save new order to server
    try {
      const orderedIds = newTodos.map(t => t.id);
      const response = await fetch(`${API_URL}/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(orderedIds),
      });
      if (!response.ok) throw new Error('Failed to save order');
      const updatedTodos = await response.json();
      setTodos(updatedTodos);
    } catch (error) {
      console.error('Error saving order:', error);
    }
  };

  const pendingTodos = todos.filter((todo) => !todo.completed);
  const completedTodos = todos.filter((todo) => todo.completed);

  const renderTodoItem = (todo, isCompleted = false) => (
    <li
      key={todo.id}
      className={`todo-item ${isCompleted ? 'completed' : ''} ${dragOverItem?.id === todo.id ? 'drag-over' : ''}`}
      draggable
      onDragStart={(e) => handleDragStart(e, todo)}
      onDragEnd={handleDragEnd}
      onDragOver={(e) => handleDragOver(e, todo)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, todo)}
    >
      <div className="drag-handle">⋮⋮</div>
      <input
        type="checkbox"
        checked={todo.completed}
        onChange={() => toggleTodo(todo.id)}
        className="checkbox"
      />
      {editingId === todo.id ? (
        <div className="edit-container">
          <input
            type="text"
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit(todo.id);
              if (e.key === 'Escape') cancelEdit();
            }}
            className="edit-input"
            autoFocus
          />
          <button onClick={() => saveEdit(todo.id)} className="save-button">
            ✓
          </button>
          <button onClick={cancelEdit} className="cancel-button">
            ✕
          </button>
        </div>
      ) : (
        <>
          <span className={`todo-text ${isCompleted ? 'completed-text' : ''}`}>{todo.text}</span>
          <div className="actions">
            <button onClick={() => startEditing(todo)} className="edit-button">
              ✏️
            </button>
            <button onClick={() => deleteTodo(todo.id)} className="delete-button">
              🗑️
            </button>
          </div>
        </>
      )}
    </li>
  );

  return (
    <div className="App">
      <div className="container">
        <div className="header">
          <h1>📝 Список дел</h1>
        </div>

        <form onSubmit={addTodo} className="add-form">
          <input
            type="text"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            placeholder="Добавить новую задачу..."
            className="add-input"
          />
          <button type="submit" className="add-button">
            Добавить
          </button>
        </form>

        {loading ? (
          <p className="loading">Загрузка...</p>
        ) : (
          <>
            {pendingTodos.length > 0 && (
              <div className="todo-section">
                <h2>Активные задачи ({pendingTodos.length})</h2>
                <ul className="todo-list">
                  {pendingTodos.map((todo) => renderTodoItem(todo, false))}
                </ul>
              </div>
            )}

            {completedTodos.length > 0 && (
              <div className="todo-section completed-section">
                <div className="section-header">
                  <h2>Выполненные ({completedTodos.length})</h2>
                  <button 
                    onClick={deleteCompletedTodos} 
                    className="delete-completed-button"
                    title="Удалить все выполненные задачи"
                  >
                    Удалить все
                  </button>
                </div>
                <ul className="todo-list">
                  {completedTodos.map((todo) => renderTodoItem(todo, true))}
                </ul>
              </div>
            )}

            {todos.length === 0 && (
              <p className="empty-message">Нет задач. Добавьте первую!</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
